import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCurrentUser, type CurrentUser } from "../../lib/auth";
import { requestEmailMagicLink, startXOAuth } from "../../lib/api";

// /settings — basic settings page. Shows the linked email + X handle and
// exposes "Link your X account" / "Link your email" CTAs for the long-tail
// single-path users (Tech Stack §6.1.3). The actual link flows reuse the
// same /api/auth/x/start and /api/auth/email/start endpoints.
//
// Post-Supabase: getCurrentUser only returns `{ id }`. Linked-email and
// linked-x-handle would need a separate /api/me/profile endpoint to surface
// here — until that ships, both CTAs are always visible.
export function SettingsRoute() {
	const [user, setUser] = useState<CurrentUser | null>(null);

	useEffect(() => {
		void getCurrentUser().then(setUser);
	}, []);

	const xHandle: string | null = null;
	const email: string | null = null;

	return (
		<div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
			<header>
				<Link
					to="/"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Dashboard
				</Link>
				<h1 className="mt-1 text-2xl font-bold">Settings</h1>
				{user && (
					<p className="mt-1 text-xs text-[var(--color-muted)]">
						Signed in as {user.id}
					</p>
				)}
			</header>

			<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
				<h2 className="text-sm font-semibold">Email</h2>
				{email ? (
					<div className="flex items-center justify-between">
						<p className="text-sm">{email}</p>
						<span className="text-xs text-[var(--color-muted)]">Linked</span>
					</div>
				) : (
					<button
						onClick={async () => {
							const e = window.prompt("Enter your email");
							if (!e) return;
							try {
								await requestEmailMagicLink(e);
								alert("Magic link sent.");
							} catch (err) {
								alert(err instanceof Error ? err.message : "Failed");
							}
						}}
						className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:bg-emerald-400"
					>
						Link an email
					</button>
				)}
			</section>

			<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
				<h2 className="text-sm font-semibold">X account</h2>
				{xHandle ? (
					<div className="flex items-center justify-between">
						<p className="text-sm">@{xHandle}</p>
						<span className="text-xs text-[var(--color-muted)]">Linked</span>
					</div>
				) : (
					<button
						onClick={async () => {
							try {
								const { authUrl } = await startXOAuth();
								sessionStorage.setItem("hf_next", "/settings");
								window.location.assign(authUrl);
							} catch (err) {
								alert(err instanceof Error ? err.message : "Failed");
							}
						}}
						className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-black hover:bg-zinc-200"
					>
						Link your X account
					</button>
				)}
			</section>

			<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
				<h2 className="text-sm font-semibold">Billing</h2>
				<div className="flex items-center justify-between">
					<p className="text-sm text-[var(--color-muted)]">
						Manage your subscription, payment method, and invoices.
					</p>
					<Link
						to="/settings/billing"
						className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
					>
						Billing
					</Link>
				</div>
			</section>
		</div>
	);
}
